import classNames from 'classnames'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { CrudStoreItemOnboarding } from 'legacy/lut/classes/models/onboarding/CrudStoreItemOnboarding'
import { EActionStatus } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { mapDataModelToIContact } from 'legacy/lut/components/widgets/public/contactEditDialog/helpers/mapDataModelToIContact'
import { EFormSource } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestDetail/actionView/dataCollector/FormSelector'
import { ConfirmDialog } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestTypes/ConfirmDialog'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo, useState } from 'react'

import { Roles } from 'clickNgo/configs/roles'
import { ContactEditor } from 'clickNgo/containers/contactEditor'
import { getContactStateKey, IContact } from 'clickNgo/entities/contact'
import { createContact, useGetContactsWithFlagsByPartnerQuery } from 'clickNgo/entities/contact/api'
import { emptyContactFlags } from 'clickNgo/entities/contactFlag'
import PartnerContacts from 'clickNgo/widgets/partnerContacts'
import { ReadOnlyField } from 'cng-common/features/form/components'
import { Button, IButtonProps } from 'core-components/library/button'
import {
  IDatePickerMultiple,
  IDatePickerRange,
  IDatePickerSingle,
} from 'core-components/library/datepicker'
import { IInputProps } from 'core-components/library/inputs'
import { Loader, LoaderOverlay } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Controller } from 'core-components/library/validation'
import { IFormCollector } from 'core/classes/models/IFormCollector'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { CrudStore } from 'core/types/crudStore'
import logger from 'core/utils/Logger'
import Logger from 'core/utils/Logger'
import { LNRFormCacheKey } from 'onboarding/api/lnrFormApi'
import { fail, success, warning } from 'onboarding/classes/services/notification'
import { getSuperAdminAudienceAccess } from 'onboarding/components/ui/audienceFilter/helpers/getSuperAdminAudienceAccess'
import { OnboardingDatePicker, OnboardingInput } from 'onboarding/components/ui/theme'
import { preparePhoneCode } from 'onboarding/components/widgets/public/forms/helpers/preparePhoneCode'
import { splitContactPhones } from 'onboarding/components/widgets/public/forms/helpers/splitContactPhones'
import { ONBOARDING_CONTACTS_CONFIG } from 'onboarding/configs/onboardingContactsConfig'
import { refreshLRProgressData } from 'onboarding/features/actions/refreshProgressData'
import { useThirdPartyType } from 'onboarding/hooks/useThirdPartyType'
import { lnrFormService } from 'onboarding/services/lnrFormService'
import { thirdPartyService } from 'onboarding/services/thirdPartyService'
import { DateHelper } from 'onboarding/utils/dateHelper'

import { handleErrorResponse, handleFailResponse } from '../helpers/handleResponse'

import { DATE_PICKER_GROUPS, LNR_FORM_COMPLETENESS_FIELDS } from './configs'
import { getEmptyLNRForm } from './helpers'
import { useLNRData } from './hooks/useLNRData'
import { TLNRForm } from './models'
import { ILNRDataProps } from './types'

import styles from './LNRData.module.scss'

const warningNoContactSelected = () => warning('Please select a contact')

const SUPER_ADMIN_ACCESS = getSuperAdminAudienceAccess()

const getRefresh3PStatusButtonAudience = (): IAudienceCondition => {
  return {
    UserRoles: [Roles.SUPER_ADMIN_ROLE, Roles.CONTRACT_OPERATIONS],
  }
}

export const LNRData = (props: ILNRDataProps) => {
  const dispatch = useAppDispatch()
  const requestId = parseInt(props.parentRequestId ?? props.requestId, 10)
  const [savedLNRFormData, setSavedLNRFormData] = useState<TLNRForm>(getEmptyLNRForm)
  const [lnrFormData, setLnrFormData] = useState<TLNRForm>(getEmptyLNRForm)

  const [isSaving, setIsSaving] = useState(false)
  const [isRefreshingThirdPartyStatus, setIsRefreshingThirdPartyStatus] = useState(false)

  const lnrFormSource = lnrFormService.getCrud(LNRFormCacheKey.lnrForm)
  const completenessCollectorFormsSource = CrudStore.get<IFormCollector>(
    CrudStoreItem.FormCollector,
    'data_collector_table'
  )

  const [isEditContactOpen, setIsEditContactOpen] = useState(false)
  const [isContactSelectOpen, setIsContactSelectOpen] = useState(false)
  const [isConfirmRoleChangeOpen, setIsConfirmRoleChangeOpen] = useState(false)

  const { contact, isContactFetching, isLNRFormFetching, refetchContact, partnerId, isDisabled } =
    useLNRData({
      initLNRFormData: (value) => {
        setSavedLNRFormData(value)
        setLnrFormData(value)
      },
      requestId: props.requestId,
      contactId: lnrFormData.ContactId || '',
      startDate: props.startDate,
    })

  const { data: editorContactState, refetch: refetchEditorContacts } =
    useGetContactsWithFlagsByPartnerQuery({ isOnboarding: true, partnerId }, { skip: !partnerId })

  const editableContact = useMemo(() => {
    const contactId = contact?.get('ContactID')

    return (
      Object.values(editorContactState?.contactsById || {}).find(
        (item) => item.contactId === contactId
      ) || null
    )
  }, [contact, editorContactState?.contactsById])

  const editableContactFlags = useMemo(() => {
    if (!editableContact) {
      return emptyContactFlags
    }

    return (
      editorContactState?.contactFlagsById[getContactStateKey(editableContact)] ??
      editorContactState?.contactFlagsById[editableContact.contactId] ??
      emptyContactFlags
    )
  }, [editableContact, editorContactState?.contactFlagsById])

  const serverWorkPhoneCode: string = contact?.get('WorkPhone_Prefix') || ''
  const workPhone: string = contact?.get('WorkPhone') || ''
  const workPhoneCode: string = useMemo(() => {
    if (!serverWorkPhoneCode) {
      return ''
    }

    const { data, error } = preparePhoneCode(serverWorkPhoneCode)
    if (error) {
      Logger.error(error)

      return ''
    }

    return data
  }, [serverWorkPhoneCode])

  const workPhoneNumber = useMemo(() => {
    return `${workPhoneCode || ''}${workPhone || ''}`.trim()
  }, [workPhoneCode, workPhone])

  const isLNRFormChanged = useMemo(
    () => JSON.stringify(savedLNRFormData) !== JSON.stringify(lnrFormData),
    [savedLNRFormData, lnrFormData]
  )

  const refreshThirdPartyStatus = useCallback(async () => {
    try {
      setIsRefreshingThirdPartyStatus(true)
      if (props.actionId) {
        const response = await thirdPartyService.refreshProfileStatus(props.actionId)
        if (response.status === 'success') {
          success(response.message)
          CrudStore.get(CrudStoreItemOnboarding.LNRFormData, 'lnrForm').removeQueries(true)
        } else {
          fail(response.message)
        }
      } else {
        warning('Can not refresh status. Action ID is not provided.')
      }
    } catch (e: any) {
      logger.error(e)
      fail('Failed to refresh status')
    } finally {
      setIsRefreshingThirdPartyStatus(false)
    }
  }, [props.actionId])

  const { thirdPartyType, isThirdPartyTypeLoading } = useThirdPartyType({
    partnerId,
    requestId,
  })
  const { audienceSettings } = props

  const saveHandler = async () => {
    if (lnrFormData.ContactId && contact) {
      const roles: string = contact.get('PR_HP_ROLE') || ''
      if (roles.includes('L&R Champion')) {
        await onSave()
      } else {
        setIsConfirmRoleChangeOpen(true)
      }
    } else {
      warningNoContactSelected()
    }
  }

  const calcCompleteness = (el: IObjectAny) => {
    if (el.ContactId) {
      return 100
    }
    let completedCount = 0
    LNR_FORM_COMPLETENESS_FIELDS.forEach((field) => {
      if (el[field]) {
        completedCount++
      }
    })
    return Number(((completedCount / LNR_FORM_COMPLETENESS_FIELDS.length) * 100).toFixed(2))
  }

  const onSave = async () => {
    setIsSaving(true)
    try {
      const isHpRoleUpdated = await onUpdateHpRole()
      if (!isHpRoleUpdated) {
        return
      }

      const formData = await lnrFormService.fetchByRequestId(requestId, LNRFormCacheKey.lnrForm)
      const itemToUpdateOrCreate = formData ? { ...formData, ...lnrFormData } : lnrFormData
      if (itemToUpdateOrCreate.ContactId) {
        await lnrFormSource[formData ? 'update' : 'create'](EFormSource.LegalAndRegulatory, [
          {
            ...itemToUpdateOrCreate,
            Completeness: calcCompleteness(itemToUpdateOrCreate),
            RequestId: requestId,
          },
        ])

        completenessCollectorFormsSource.triggerOnChange()
        lnrFormSource.triggerOnChange()
        dispatch(refreshLRProgressData())
        setSavedLNRFormData(lnrFormData)
        success('Changes saved')
      } else {
        warningNoContactSelected()
      }
    } catch (e: unknown) {
      logger.error(e)
      fail('Failed to save the form')
    } finally {
      setIsSaving(false)
    }
  }

  const onUpdateHpRole = async (): Promise<boolean> => {
    if (!contact) {
      warningNoContactSelected()
      return false
    }

    const roles: string = contact.get('PR_HP_ROLE') || ''
    if (roles.includes('L&R Champion')) {
      return true
    }

    // mutate a draft so a failed request leaves the contact model untouched
    const draft = contact.clone()
    draft.set('PR_HP_ROLE', roles ? `${roles};L&R Champion` : 'L&R Champion')
    draft.set('OnboardingRequestId', requestId)
    if (!draft.get('Source')) {
      draft.set('Source', props.appSource || 'Onboarding')
    }

    // The contact may arrive with the whole number in a single field (no prefix).
    // Split it into country code + body so the payload matches what the backend expects.
    splitContactPhones(draft)

    const createContactResponse = await createContact(mapDataModelToIContact(draft))
    const { status, message, data: responseData } = createContactResponse

    if (status === 'fail') {
      handleFailResponse(responseData)
      return false
    }

    if (status === 'error') {
      handleErrorResponse(message)
      return false
    }

    return true
  }

  const onChange = (field: string) => (value: string | number) => {
    setLnrFormData((item) => ({ ...item, [field]: value }))
  }

  const handleContactSelect = useCallback((selectedContact: IContact) => {
    setLnrFormData((prev) => ({ ...prev, ContactId: selectedContact.contactId }))
    setIsContactSelectOpen(false)
  }, [])

  const refetchEditedContact = useCallback(async () => {
    await Promise.all([
      refetchEditorContacts(),
      contact ? refetchContact(contact.get('ContactID')) : Promise.resolve(),
    ])
  }, [contact, refetchContact, refetchEditorContacts])

  const isActionInError = props.actionStatus === EActionStatus.Error

  return (
    <>
      <Controller>
        <div className={classNames(styles.content)}>
          <LoaderOverlay show={isSaving || isRefreshingThirdPartyStatus || isLNRFormFetching} />
          <div className={styles.row}>
            <div className={styles.col}>
              <div className={styles.sectionHeader}>
                <p className={styles.title}>Contact information</p>
              </div>
              {isContactFetching ? (
                <Loader />
              ) : contact && contact.get('ContactID') ? (
                <div className={styles.contactInformationWrapper}>
                  <div className={styles.contactFields}>
                    <ReadOnlyField label={'First Name'} value={contact.get('FirstName') ?? ''} />
                    <ReadOnlyField label={'Last Name'} value={contact.get('LastName') ?? ''} />
                    <ReadOnlyField label={'Email'} value={contact.get('Email') ?? ''} />
                    <ReadOnlyField
                      label={'Phone number'}
                      value={workPhoneNumber ? `+${workPhoneNumber}` : ''}
                    />
                  </div>
                  <div className={styles.contactActions}>
                    <VisualAudienceExtended<IButtonProps>
                      write={audienceSettings}
                      component={Button}
                      onClick={() => setIsContactSelectOpen(true)}
                      disabled={(props.disabled || isDisabled) && !isActionInError}
                    >
                      Reselect contact
                    </VisualAudienceExtended>
                    <VisualAudienceExtended<IButtonProps>
                      write={audienceSettings}
                      component={Button}
                      variant={'outlined'}
                      className={styles.contactActionsEnd}
                      onClick={() => {
                        setIsEditContactOpen(true)
                      }}
                      disabled={(props.disabled || isDisabled) && !isActionInError}
                    >
                      Edit Contact information
                    </VisualAudienceExtended>
                  </div>
                </div>
              ) : (
                <div>
                  <p className={styles.wrongText}>Contact isn’t assigned to this request</p>
                  <VisualAudienceExtended
                    write={audienceSettings}
                    component={Button}
                    margin={'md'}
                    onClick={() => setIsContactSelectOpen(true)}
                    disabled={props.disabled}
                  >
                    Select contact
                  </VisualAudienceExtended>
                </div>
              )}
            </div>
            <div className={styles.col}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionHeading}>
                  <p className={styles.title}>L&R process</p>
                  {props.actionId && !thirdPartyType && !isThirdPartyTypeLoading && (
                    <span className={styles.sectionHint}>Profile is not created yet</span>
                  )}
                </div>
                {props.actionId ? (
                  <VisualAudienceExtended<IButtonProps>
                    write={getRefresh3PStatusButtonAudience()}
                    component={Button}
                    variant={'outlined'}
                    onClick={refreshThirdPartyStatus}
                    loading={isThirdPartyTypeLoading}
                    disabled={!thirdPartyType}
                  >
                    Refresh 3P status
                  </VisualAudienceExtended>
                ) : null}
              </div>
              <div className={styles.fieldGroup}>
                <p className={styles.groupTitle}>Statuses</p>
                <VisualAudienceExtended<IInputProps>
                  component={OnboardingInput}
                  write={SUPER_ADMIN_ACCESS}
                  margin={'md'}
                  label={'L&R Process Detailed Status'}
                  value={lnrFormData.ApprovalStatus ?? ''}
                  onChange={onChange('ApprovalStatus')}
                  disabled={props.disabled}
                />
                <VisualAudienceExtended<IInputProps>
                  component={OnboardingInput}
                  write={SUPER_ADMIN_ACCESS}
                  margin={'md'}
                  label={'Internal Approval Status'}
                  value={lnrFormData.InternalApprovalStatus ?? ''}
                  onChange={onChange('InternalApprovalStatus')}
                  disabled={props.disabled}
                />
              </div>
              {DATE_PICKER_GROUPS.map((group) => (
                <div key={group.title} className={styles.fieldGroup}>
                  <p className={styles.groupTitle}>{group.title}</p>
                  {group.pickers.map((el) => (
                    <VisualAudienceExtended<
                      IDatePickerSingle | IDatePickerRange | IDatePickerMultiple
                    >
                      component={OnboardingDatePicker}
                      write={audienceSettings}
                      disabled={props.disabled}
                      margin={'md'}
                      key={el.field}
                      mode={'single'}
                      value={
                        lnrFormData[el.field]
                          ? DateHelper.convertServerISOToDate(lnrFormData[el.field]) || undefined
                          : undefined
                      }
                      onChange={(value: Date) => {
                        let formattedDate = ''
                        if (value) {
                          formattedDate = DateHelper.convertDateToServerISO(value)
                        }

                        onChange(el.field)(formattedDate)
                      }}
                      label={el.label}
                    />
                  ))}
                </div>
              ))}
              {isEditContactOpen && editableContact && (
                <ContactEditor
                  contact={editableContact}
                  contactFlags={editableContactFlags}
                  isVisible
                  source={props.appSource}
                  isReadOnly={(props.disabled || isDisabled) && !isActionInError}
                  isNewContact={false}
                  isOnboarding
                  onClose={() => setIsEditContactOpen(false)}
                  onContactUpdate={refetchEditedContact}
                  onContactFlagsUpdate={refetchEditedContact}
                />
              )}
              {isEditContactOpen && !editableContact && (
                <ModalDialog
                  show={isEditContactOpen}
                  onClose={() => setIsEditContactOpen(false)}
                  size="md"
                  content={() => <Loader />}
                />
              )}
            </div>
          </div>
          <div className={styles.btnWrapper}>
            {isLNRFormChanged && (
              <span className={styles.unsavedChanges}>
                <span className={styles.unsavedDot} />
                {lnrFormData.ContactId ? 'Unsaved changes' : 'Select a contact to save'}
              </span>
            )}
            <VisualAudienceExtended<IButtonProps>
              write={audienceSettings}
              component={Button}
              disabled={
                props.disabled || !lnrFormData.ContactId || isContactFetching || !isLNRFormChanged
              }
              onClick={saveHandler}
            >
              Save
            </VisualAudienceExtended>
          </div>
        </div>
        {isContactSelectOpen && partnerId && (
          <ModalDialog
            show={isContactSelectOpen}
            onClose={() => setIsContactSelectOpen(false)}
            size="xl"
            content={() => (
              <PartnerContacts
                partnerId={partnerId}
                source={props.appSource}
                config={ONBOARDING_CONTACTS_CONFIG}
                onContactSelect={handleContactSelect}
              />
            )}
          />
        )}
      </Controller>
      <ConfirmDialog
        show={isConfirmRoleChangeOpen}
        text={
          'Changes will be sent to the profiling team. PR_HP_ROLE of the contact will be changed to L&R Champion'
        }
        onSubmit={async () => {
          await onSave()
          setIsConfirmRoleChangeOpen(false)
        }}
        onCancel={() => setIsConfirmRoleChangeOpen(false)}
      />
    </>
  )
}
